// Live-патчи rich_card / artifact_card (socket *:ready / *:failed / SSE card_upsert)
// до PowerSync sync. Shared store для main chat + side panel.

import type { TutorRichCard } from "@studyhub/shared/contracts/tutor";
import { useEffect, useState } from "react";
import { track } from "@/lib/observability/analytics";

export type CardLivePatch = Partial<
	Pick<TutorRichCard, "status" | "summary" | "deepLink" | "title" | "errorMessage" | "meta">
>;

const patchesByCardId = new Map<string, CardLivePatch>();
/** Полные pending-карточки (меню / SSE) для orphan-UI до PowerSync. */
const pendingCardsById = new Map<string, TutorRichCard>();
/**
 * Sticky якорь: cardId → assistant messageId.
 * Контент сообщения (force) и upgrade temp→uuid перезаписывают; иначе
 * voice/new turns не угоняют якорь.
 */
const cardAnchorById = new Map<string, string>();
/**
 * Карточка уже «приземлилась» в ленте. Ready+settled без якоря в списке
 * НЕ вешаем на последнего ассистента — иначе конспект/карточки/тест
 * появляются после каждого нового ответа (ghost + scroll jump).
 */
const settledCardIds = new Set<string>();
/** cardId → уже отправили tutor_card_ready (dedup). */
const readyTrackedIds = new Set<string>();
const listeners = new Set<() => void>();

function notify(): void {
	for (const l of listeners) l();
}

function emitCardReadyIfNeeded(cardId: string, kind: string, nextStatus: string | undefined): void {
	if (nextStatus !== "ready" || readyTrackedIds.has(cardId)) return;
	readyTrackedIds.add(cardId);
	track("tutor_card_ready", { kind, id: cardId });
}

function isTempMessageId(id: string): boolean {
	return id.startsWith("a-") || id.startsWith("voice-");
}

/** Якорь карточки на assistant message. `force` — содержимое сообщения = истина. */
export function bindRichCardAnchor(
	cardId: string,
	messageId: string,
	opts?: { force?: boolean },
): void {
	if (!cardId || !messageId) return;
	const prev = cardAnchorById.get(cardId);
	if (!prev || opts?.force) {
		cardAnchorById.set(cardId, messageId);
		return;
	}
	// Optimistic `a-…` → persisted uuid после PowerSync.
	if (isTempMessageId(prev) && !isTempMessageId(messageId)) {
		cardAnchorById.set(cardId, messageId);
	}
}

export function markRichCardSettled(cardId: string): void {
	if (cardId) settledCardIds.add(cardId);
}

export function isRichCardSettled(cardId: string): boolean {
	return Boolean(cardId) && settledCardIds.has(cardId);
}

export function trackRichCardPending(
	card: TutorRichCard,
	opts?: { anchorMessageId?: string },
): void {
	const status = card.status ?? "pending";
	pendingCardsById.set(card.id, { ...card, status });
	if (!patchesByCardId.has(card.id)) {
		patchesByCardId.set(card.id, { status });
	}
	if (opts?.anchorMessageId) bindRichCardAnchor(card.id, opts.anchorMessageId);
	notify();
}

function mergePatch(prev: CardLivePatch, patch: CardLivePatch): CardLivePatch {
	return {
		...prev,
		...patch,
		...(prev.meta || patch.meta ? { meta: { ...(prev.meta ?? {}), ...(patch.meta ?? {}) } } : {}),
	};
}

export function applyCardLivePatch(cardId: string, patch: CardLivePatch): void {
	const prev = patchesByCardId.get(cardId) ?? {};
	const next = mergePatch(prev, patch);
	patchesByCardId.set(cardId, next);

	const pending = pendingCardsById.get(cardId);
	if (pending) {
		const mergedMeta =
			pending.meta || next.meta ? { ...(pending.meta ?? {}), ...(next.meta ?? {}) } : undefined;
		pendingCardsById.set(cardId, {
			...pending,
			...next,
			...(mergedMeta ? { meta: mergedMeta } : {}),
		});
	}

	const kind =
		pending?.kind ?? (typeof next.meta?.kind === "string" ? next.meta.kind : "action_result");
	emitCardReadyIfNeeded(cardId, kind, next.status);

	notify();
}

/** Upsert полной карточки из SSE `card_upsert` (pending → ready/failed). */
export function upsertLiveRichCard(card: TutorRichCard): void {
	pendingCardsById.set(card.id, card);
	const prev = patchesByCardId.get(card.id) ?? {};
	const patch: CardLivePatch = {
		status: card.status,
		...(card.title !== undefined ? { title: card.title } : {}),
		...(card.summary !== undefined ? { summary: card.summary } : {}),
		...(card.deepLink !== undefined ? { deepLink: card.deepLink } : {}),
		...(card.errorMessage !== undefined ? { errorMessage: card.errorMessage } : {}),
		...(card.meta !== undefined ? { meta: card.meta } : {}),
	};
	patchesByCardId.set(card.id, { ...prev, ...patch });
	emitCardReadyIfNeeded(card.id, card.kind, card.status);
	notify();
}

export function dismissRichCard(cardId: string): void {
	applyCardLivePatch(cardId, { status: "dismissed" });
}

/** Мержит persisted/stream cards с live-патчами. */
export function mergeRichCardsWithLive(cards: TutorRichCard[]): TutorRichCard[] {
	if (cards.length === 0 && patchesByCardId.size === 0) return cards;
	return cards
		.map((c) => {
			const patch = patchesByCardId.get(c.id);
			if (!patch) return c;
			const meta = c.meta || patch.meta ? { ...(c.meta ?? {}), ...(patch.meta ?? {}) } : undefined;
			return {
				...c,
				...patch,
				...(meta ? { meta } : {}),
			};
		})
		.filter((c) => c.status !== "dismissed");
}

/**
 * Pending/ready cards без part в сообщении (меню / SSE до sync) — для orphan-UI.
 * knownIds — уже показанные в ленте (не дублировать).
 */
export function orphanRichCards(knownIds: Iterable<string>): TutorRichCard[] {
	const known = new Set(knownIds);
	const out: TutorRichCard[] = [];
	for (const [id, base] of pendingCardsById) {
		if (known.has(id)) continue;
		const patch = patchesByCardId.get(id);
		const merged = patch ? { ...base, ...patch } : base;
		if (merged.status === "dismissed") continue;
		out.push(merged);
	}
	return out;
}

function isVoiceLiveMessageId(id: string | undefined): boolean {
	if (!id) return false;
	return id.startsWith("voice-") || id.startsWith("voice-a-") || id.startsWith("voice-u-");
}

/**
 * Куда вешать orphans без sticky-якоря: предпочитаем последнего ассистента,
 * у которого уже есть карточки; иначе — последнего НЕ-voice ассистента
 * (чтобы голосовой ход не перехватывал текстовые create_job).
 */
function resolveUnanchoredOrphanTargetIdx<
	T extends { id?: string; role: string; richCards?: TutorRichCard[] },
>(list: T[]): number {
	let lastWithCards = -1;
	let lastNonVoice = -1;
	let lastAssistant = -1;
	for (let i = list.length - 1; i >= 0; i--) {
		const m = list[i];
		if (m?.role !== "assistant") continue;
		if (lastAssistant < 0) lastAssistant = i;
		if (lastNonVoice < 0 && !isVoiceLiveMessageId(m.id)) lastNonVoice = i;
		if (lastWithCards < 0 && (m.richCards?.length ?? 0) > 0) lastWithCards = i;
		if (lastWithCards >= 0 && lastNonVoice >= 0) break;
	}
	if (lastWithCards >= 0) return lastWithCards;
	if (lastNonVoice >= 0) return lastNonVoice;
	return lastAssistant;
}

/** Live-merge + orphan attach с sticky-якорем на исходный assistant message. */
export function applyLiveRichCardsToMessages<
	T extends { id?: string; role: string; richCards?: TutorRichCard[] },
>(list: T[], extraKnownIds: Iterable<string> = []): T[] {
	const knownIds = new Set<string>(extraKnownIds);
	for (const m of list) {
		if (m.role === "assistant" && m.id) {
			for (const c of m.richCards ?? []) {
				knownIds.add(c.id);
				// Содержимое сообщения — источник истины (в т.ч. uuid после PS).
				bindRichCardAnchor(c.id, m.id, { force: true });
				settledCardIds.add(c.id);
				// Persisted part есть — pending больше не нужен как orphan-источник.
				pendingCardsById.delete(c.id);
			}
		} else {
			for (const c of m.richCards ?? []) knownIds.add(c.id);
		}
	}
	const orphans = orphanRichCards(knownIds);
	if (orphans.length === 0) {
		return list.map((m) => {
			const merged =
				m.richCards && m.richCards.length > 0 ? mergeRichCardsWithLive(m.richCards) : m.richCards;
			return merged !== m.richCards ? { ...m, richCards: merged } : m;
		});
	}

	const byAnchor = new Map<string, TutorRichCard[]>();
	const unanchored: TutorRichCard[] = [];
	for (const card of orphans) {
		const anchor = cardAnchorById.get(card.id);
		if (anchor) {
			const bucket = byAnchor.get(anchor) ?? [];
			bucket.push(card);
			byAnchor.set(anchor, bucket);
		} else {
			unanchored.push(card);
		}
	}

	const idToIdx = new Map<string, number>();
	for (let i = 0; i < list.length; i++) {
		const id = list[i]?.id;
		if (id) idToIdx.set(id, i);
	}

	// Якорь мёртв (optimistic снят): НЕ перевешиваем на новый ответ — ждём
	// persisted uuid / force-rebind. Иначе ghost-карточки после каждого хода.
	for (const anchorId of [...byAnchor.keys()]) {
		if (!idToIdx.has(anchorId)) byAnchor.delete(anchorId);
	}
	const fallbackOrphans = unanchored.filter((c) => !settledCardIds.has(c.id));
	const fallbackIdx = fallbackOrphans.length > 0 ? resolveUnanchoredOrphanTargetIdx(list) : -1;

	return list.map((m, i) => {
		const merged =
			m.richCards && m.richCards.length > 0 ? mergeRichCardsWithLive(m.richCards) : m.richCards;
		const anchored = m.id ? (byAnchor.get(m.id) ?? []) : [];
		const fallback = i === fallbackIdx ? fallbackOrphans : [];
		const extra = [...anchored, ...fallback];
		if (extra.length > 0) {
			for (const c of extra) {
				if (m.id) bindRichCardAnchor(c.id, m.id);
				settledCardIds.add(c.id);
			}
			return { ...m, richCards: [...(merged ?? []), ...extra] };
		}
		if (merged !== m.richCards) {
			return { ...m, richCards: merged };
		}
		return m;
	});
}

/** React-подписка на live-патчи (ре-рендер при ready/failed/dismissed). */
export function useCardLiveVersion(): number {
	const [version, setVersion] = useState(0);
	useEffect(() => {
		const onChange = () => setVersion((v) => v + 1);
		listeners.add(onChange);
		return () => {
			listeners.delete(onChange);
		};
	}, []);
	return version;
}

/** @internal vitest only */
export function resetCardLiveForTests(): void {
	patchesByCardId.clear();
	pendingCardsById.clear();
	cardAnchorById.clear();
	settledCardIds.clear();
	readyTrackedIds.clear();
}

/** Sticky-якорь карточки (для legacy artifact path и тестов). */
export function getRichCardAnchor(cardId: string): string | undefined {
	return cardAnchorById.get(cardId);
}
